import { Button, ButtonText } from '@/components/ui/button'
import { Text } from '@/components/ui/text'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import { completeOnboardingThunk } from '@/redux/slices/onboardingSlice'
import { useAppDispatch } from '@/redux/store'
import { BRAND_COLOR } from '@/theme'
import { Ionicons } from '@expo/vector-icons'
import { router } from 'expo-router'
import { useCallback, useRef, useState } from 'react'
import { FlatList, Platform, Pressable, useWindowDimensions, View, type ListViewToken } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import OnboardingSlide, { type OnboardingSlideData } from './OnboardingSlide'

const PRIMARY_COLOR = BRAND_COLOR.light

const slides: OnboardingSlideData[] = [
  {
    icon: <Ionicons name="sparkles" size={80} color={PRIMARY_COLOR} />,
    title: '欢迎来到新版金数据',
    subtitle: '我们带来了令人兴奋的新功能',
  },
  {
    icon: <Ionicons name="flash" size={80} color={PRIMARY_COLOR} />,
    title: 'AI 助力创作',
    subtitle: '在手机上使用 AI 创建表单、生成图片，效率提升 10 倍',
  },
  {
    icon: <Ionicons name="brush" size={80} color={PRIMARY_COLOR} />,
    title: '全新设计语言',
    subtitle:
      Platform.OS === 'ios' ? '焕然一新的界面，完美适配 iOS 26 液态玻璃效果' : '焕然一新的界面，带来更流畅的使用体验',
  },
]

export default function OnboardingCarousel() {
  const dispatch = useAppDispatch()
  const { width } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  // Pages sit beside the iPhone Duo side bar, so each one is as wide as the space left of it
  const slideWidth = width - insets.left - insets.right
  const flatListRef = useRef<FlatList>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [backgroundColor, inactiveDotColor] = usePlatformColor('systemBackground', 'opaqueSeparator')

  const isLastSlide = currentIndex === slides.length - 1

  const onViewableItemsChanged = useCallback(({ viewableItems }: { viewableItems: ListViewToken[] }) => {
    if (viewableItems.length > 0 && viewableItems[0] && viewableItems[0].index != null) {
      setCurrentIndex(viewableItems[0].index)
    }
  }, [])

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 50,
  }).current

  const handleComplete = useCallback(() => {
    dispatch(completeOnboardingThunk())
    router.replace('/login')
  }, [dispatch])

  const handleNext = useCallback(() => {
    if (isLastSlide) {
      handleComplete()
    } else {
      flatListRef.current?.scrollToIndex({ index: currentIndex + 1, animated: true })
    }
  }, [currentIndex, handleComplete, isLastSlide])

  const handleSkip = useCallback(() => {
    handleComplete()
  }, [handleComplete])

  const renderItem = useCallback(
    ({ item }: { item: OnboardingSlideData }) => <OnboardingSlide {...item} width={slideWidth} />,
    [slideWidth]
  )

  const keyExtractor = useCallback((_: OnboardingSlideData, index: number) => `slide-${index}`, [])

  return (
    <View className="flex-1" style={{ backgroundColor, paddingLeft: insets.left, paddingRight: insets.right }}>
      {/* Header with Skip button */}
      <View className="h-14 px-6 flex-row justify-end items-center" style={{ marginTop: insets.top }}>
        {!isLastSlide && (
          <Pressable onPress={handleSkip} hitSlop={8}>
            <Text className="text-[17px]" style={{ color: PRIMARY_COLOR }}>
              跳过
            </Text>
          </Pressable>
        )}
      </View>

      {/* Slides */}
      <FlatList
        ref={flatListRef}
        data={slides}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        bounces={false}
        getItemLayout={(_, index) => ({
          length: slideWidth,
          offset: slideWidth * index,
          index,
        })}
      />

      {/* Bottom section */}
      <View className="px-6 items-center gap-6" style={{ paddingBottom: insets.bottom + 50 }}>
        {/* Page indicators */}
        <View className="flex-row gap-2">
          {slides.map((_, index) => (
            <View
              key={index}
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: index === currentIndex ? PRIMARY_COLOR : inactiveDotColor }}
            />
          ))}
        </View>

        {/* Button */}
        <Button className="w-full h-[50px] rounded-xl" style={{ backgroundColor: PRIMARY_COLOR }} onPress={handleNext}>
          <ButtonText className="text-[17px] font-semibold">{isLastSlide ? '开始体验' : '下一步'}</ButtonText>
        </Button>
      </View>
    </View>
  )
}
